import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getCommerceListService,
  getPlateStatusService,
} from "modules/monitoreo/services/placasService";
import { isValidPlate, normalizePlate, plateCommerceId } from "../model/plateModel";
export const usePlacas = () => {
  const [comercios, setComercios] = useState([]),
    [loadingComercios, setLoadingComercios] = useState(false),
    [commerceError, setCommerceError] = useState(null),
    [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState({ idCommerce: "", placa: "" }),
    [loading, setLoading] = useState(false),
    [resultadoPlaca, setResultadoPlaca] = useState(null),
    [error, setError] = useState(null),
    [ultimoError, setUltimoError] = useState(null),
    [historialConsultas, setHistorialConsultas] = useState([]);
  const mounted = useRef(false),
    listRequest = useRef(null),
    active = useRef(null),
    revision = useRef(0),
    historyId = useRef(0);
  const loadComercios = useCallback(async () => {
    if (listRequest.current) return listRequest.current;
    setLoadingComercios(true);
    setCommerceError(null);
    listRequest.current = (async () => {
      try {
        const result = await getCommerceListService();
        if (mounted.current) {
          if (result.success) {
            setComercios(result.data);
            setLastUpdated(new Date().toISOString());
          } else setCommerceError(result.error);
        }
        return result;
      } catch (error) {
        if (mounted.current) setCommerceError(error.message);
        return { success: false, error: error.message };
      } finally {
        listRequest.current = null;
        if (mounted.current) setLoadingComercios(false);
      }
    })();
    return listRequest.current;
  }, []);
  useEffect(() => {
    mounted.current = true;
    loadComercios();
    return () => {
      mounted.current = false;
      revision.current++;
      active.current?.controller.abort();
    };
  }, [loadComercios]);
  const invalidate = useCallback(() => {
    revision.current++;
    active.current?.controller.abort();
    active.current = null;
    setLoading(false);
    setResultadoPlaca(null);
    setError(null);
    setUltimoError(null);
  }, []);
  const setFiltro = (name, value) => {
    if (name === "idCommerce" || name === "placa") invalidate();
    setFiltros((previous) => ({
      ...previous,
      [name]: name === "placa" ? normalizePlate(value) : value,
    }));
  };
  const consultarPlaca = useCallback((commerceId, plate) => {
    const id = plateCommerceId(commerceId),
      formatted = normalizePlate(plate);
    if (id === null || !isValidPlate(formatted)) {
      const message = id === null ? "Seleccione un comercio" : "Use una placa como ABC123 o ABC12A";
      setError(message);
      return Promise.resolve({ success: false, error: message });
    }
    const key = `${id}/${formatted}`;
    if (active.current?.key === key) return active.current.promise;
    active.current?.controller.abort();
    const requestRevision = ++revision.current,
      controller = new AbortController();
    setLoading(true);
    setError(null);
    setResultadoPlaca(null);
    setUltimoError(null);
    const request = { key, controller, promise: null };
    active.current = request;
    request.promise = (async () => {
      let result;
      try {
        result = await getPlateStatusService(id, formatted, { signal: controller.signal });
      } catch (error) {
        result = { success: false, error: error.message, canRetry: true };
      }
      if (!mounted.current || requestRevision !== revision.current || result.canceled)
        return { success: false, ignored: true };
      const receivedAt = new Date().toISOString();
      const data = result.data ? { ...result.data, receivedAt, commerceId: id } : null;
      setResultadoPlaca(data);
      if (!result.success && !result.isNotGopass) {
        setError(result.error || result.message || "No se pudo completar la consulta");
        setUltimoError({ ...result, commerceId: id, placa: formatted });
      }
      setHistorialConsultas((previous) => [
        { id: ++historyId.current, commerceId: id, plate: formatted, receivedAt, ...result, data },
        ...previous,
      ]);
      setLoading(false);
      active.current = null;
      return { ...result, data };
    })();
    return request.promise;
  }, []);
  const reintentarUltimaConsulta = () =>
    ultimoError?.canRetry === true && plateCommerceId(ultimoError.commerceId) !== null
      ? consultarPlaca(ultimoError.commerceId, ultimoError.placa)
      : Promise.resolve(null);
  const clearFilters = () => {
    invalidate();
    setFiltros({ idCommerce: "", placa: "" });
  };
  const stats = useMemo(
    () => ({
      totalComercios: comercios.length,
      totalConsultas: historialConsultas.length,
      resultados: historialConsultas.filter((item) => item.success).length,
    }),
    [comercios, historialConsultas]
  );
  return {
    comercios,
    loadingComercios,
    commerceError,
    lastUpdated,
    loadComercios,
    filtros,
    setFiltro,
    loading,
    resultadoPlaca,
    error,
    ultimoError,
    historialConsultas,
    stats,
    consultarPlaca,
    reintentarUltimaConsulta,
    clearFilters,
  };
};
